import { useState, useEffect, useMemo, useCallback } from 'react';
import type { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { ExternalLink, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import type { DealMissingOutcomeRow } from '@/hooks/use-reports';
import { useDealsMissingOutcomeReport } from '@/hooks/use-reports';
import { formatDateEDT } from '@/lib/date';
import type { ChipOption } from './components/chip-group';
import { ChipGroup } from './components/chip-group';

const REPORT_PATH = '/app/reports/deals-missing-outcome';

function DomainsCell({ value, data }: ICellRendererParams<DealMissingOutcomeRow, string[]>) {
  if (!Array.isArray(value) || value.length === 0 || !data)
    return null;

  const domains = value.join(', ');

  return (
    <a
      href={`/app/deals/${data.id}`}
      target="_blank"
      rel="noopener noreferrer"
      title={domains}
      className="inline-flex max-w-full items-center gap-1.5 hover:underline"
    >
      <span className="min-w-0 truncate">{domains}</span>
      <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" />
    </a>
  );
}

function DateCell({ value }: ICellRendererParams<DealMissingOutcomeRow, string | null>) {
  if (!value)
    return null;
  return formatDateEDT(value);
}

function dealsMissingOutcomeColDefs(): ColDef<DealMissingOutcomeRow>[] {
  return [
    { field: 'domains', headerName: 'Domains', filter: true, sortable: true, resizable: true, flex: 1, minWidth: 260, cellRenderer: DomainsCell },
    { field: 'deal_stage', headerName: 'Stage', filter: true, sortable: true, resizable: true, width: 200 },
    { field: 'deal_type', headerName: 'Type', filter: true, sortable: true, resizable: true, width: 180 },
    { field: 'created_at', headerName: 'Created', filter: true, sortable: true, resizable: true, width: 200, cellRenderer: DateCell, sort: 'asc' },
    { field: 'updated_at', headerName: 'Updated', filter: true, sortable: true, resizable: true, width: 200, cellRenderer: DateCell },
  ];
}

const EXCEL_HEADERS: Array<{ key: keyof DealMissingOutcomeRow; label: string }> = [
  { key: 'id', label: 'Deal ID' },
  { key: 'domains', label: 'Domains' },
  { key: 'deal_stage', label: 'Stage' },
  { key: 'deal_type', label: 'Type' },
  { key: 'created_at', label: 'Created' },
  { key: 'updated_at', label: 'Updated' },
];

function processRowForExcel(row: DealMissingOutcomeRow) {
  const processed: Record<string, string> = {};
  for (const { key, label } of EXCEL_HEADERS) {
    const value = row[key];
    if (Array.isArray(value)) {
      processed[label] = value.join(', ');
    } else if (key === 'created_at' || key === 'updated_at') {
      processed[label] = value ? formatDateEDT(value) : '';
    } else {
      processed[label] = value ?? '';
    }
  }
  return processed;
}

function getMaxLength(rows: Array<Record<string, string>>, key: string) {
  return Math.max(0, ...rows.map((row) => (row[key] ?? '').length));
}

const TLD_OPTIONS: ChipOption[] = [
  { value: 'bank', label: '.Bank' },
  { value: 'insurance', label: '.Insurance' },
];

function dealTlds(deal: DealMissingOutcomeRow): string[] {
  return [...new Set(deal.domains.map((d) => d.split('.').pop()?.toLowerCase() ?? '').filter(Boolean))];
}

/** Toggle a value in a Set; an empty selection is treated as "all", so reset to all. */
function toggleInSet(prev: Set<string>, value: string, all: string[]): Set<string> {
  const next = new Set(prev);
  if (next.has(value))
    next.delete(value);
  else next.add(value);
  return next.size === 0 ? new Set(all) : next;
}

function countBy(deals: DealMissingOutcomeRow[], pick: (d: DealMissingOutcomeRow) => string[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const deal of deals) {
    for (const key of pick(deal))
      counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

export default function DealsMissingOutcomeReport() {
  const { setLabel, removeLabel } = useBreadcrumb();
  const { data: reportData, isLoading, error } = useDealsMissingOutcomeReport();
  const [searchQuery, setSearchQuery] = useState('');
  // null = nothing toggled yet, which means "all selected" once options are known.
  const [stageSelection, setStageSelection] = useState<Set<string> | null>(null);
  const [typeSelection, setTypeSelection] = useState<Set<string> | null>(null);
  const [selectedTlds, setSelectedTlds] = useState<Set<string>>(() => new Set(TLD_OPTIONS.map((o) => o.value)));

  const columns = useMemo(() => dealsMissingOutcomeColDefs(), []);
  const allDeals = useMemo(() => reportData?.deals ?? [], [reportData]);

  // Chip options come from the data so we only show stages/types that actually occur.
  const stageOptions = useMemo<ChipOption[]>(
    () => [...new Set(allDeals.map((d) => d.deal_stage))].sort().map((v) => ({ value: v, label: v })),
    [allDeals],
  );
  const typeOptions = useMemo<ChipOption[]>(
    () => [...new Set(allDeals.map((d) => d.deal_type))].sort().map((v) => ({ value: v, label: v })),
    [allDeals],
  );
  const allStages = useMemo(() => stageOptions.map((o) => o.value), [stageOptions]);
  const allTypes = useMemo(() => typeOptions.map((o) => o.value), [typeOptions]);
  const allTlds = useMemo(() => TLD_OPTIONS.map((o) => o.value), []);

  const selectedStages = useMemo(() => stageSelection ?? new Set(allStages), [stageSelection, allStages]);
  const selectedTypes = useMemo(() => typeSelection ?? new Set(allTypes), [typeSelection, allTypes]);

  useEffect(() => {
    setLabel(REPORT_PATH, {
      name: 'Deals Missing Outcome',
      path: REPORT_PATH,
    });
    return () => removeLabel(REPORT_PATH);
  }, [setLabel, removeLabel]);

  useEffect(() => {
    if (error)
      toast.error(error.message);
  }, [error]);

  const deals = useMemo(
    () => allDeals.filter((d) =>
      selectedStages.has(d.deal_stage)
      && selectedTypes.has(d.deal_type)
      && dealTlds(d).some((tld) => selectedTlds.has(tld)),
    ),
    [allDeals, selectedStages, selectedTypes, selectedTlds],
  );

  // Counts reflect the other active filters so each chip shows what it would contribute.
  const stageCounts = useMemo(
    () => countBy(
      allDeals.filter((d) => selectedTypes.has(d.deal_type) && dealTlds(d).some((t) => selectedTlds.has(t))),
      (d) => [d.deal_stage],
    ),
    [allDeals, selectedTypes, selectedTlds],
  );
  const typeCounts = useMemo(
    () => countBy(
      allDeals.filter((d) => selectedStages.has(d.deal_stage) && dealTlds(d).some((t) => selectedTlds.has(t))),
      (d) => [d.deal_type],
    ),
    [allDeals, selectedStages, selectedTlds],
  );
  const tldCounts = useMemo(
    () => countBy(
      allDeals.filter((d) => selectedStages.has(d.deal_stage) && selectedTypes.has(d.deal_type)),
      dealTlds,
    ),
    [allDeals, selectedStages, selectedTypes],
  );

  const toggleStage = useCallback((v: string) => setStageSelection((prev) => toggleInSet(prev ?? new Set(allStages), v, allStages)), [allStages]);
  const toggleType = useCallback((v: string) => setTypeSelection((prev) => toggleInSet(prev ?? new Set(allTypes), v, allTypes)), [allTypes]);
  const toggleTld = useCallback((v: string) => setSelectedTlds((prev) => toggleInSet(prev, v, allTlds)), [allTlds]);

  const handleDownloadExcel = () => {
    if (deals.length === 0) {
      toast.error('No report data available to download.');
      return;
    }

    try {
      const rows = deals.map((deal) => processRowForExcel(deal));
      const headerLabels = EXCEL_HEADERS.map((h) => h.label);

      const ws = utils.json_to_sheet(rows, { header: headerLabels });
      utils.sheet_add_aoa(ws, [headerLabels], { origin: 'A1' });
      ws['!cols'] = headerLabels.map((label) => ({
        wch: Math.min(Math.max(label.length, getMaxLength(rows, label)) + 2, 80),
      }));

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Deals Missing Outcome');
      writeFile(wb, 'deals_missing_outcome_report.xlsx');

      toast.success('Excel report download started!');
    } catch (excelError) {
      console.error('Error generating Excel file:', excelError);
      toast.error('Failed to create Excel file.');
    }
  };

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">Deals Missing Outcome</h1>
          <p className="text-sm text-muted-foreground">
            All deals not in the "Migrated" stage that have no outcome recorded, oldest first.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadExcel}
          disabled={isLoading || deals.length === 0}
          className="gap-2"
        >
          <FileSpreadsheet className="size-4" />
          Export
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4 border-dashed">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-x-8 gap-y-4">
              <ChipGroup
                label="Stage"
                options={stageOptions}
                selected={selectedStages}
                onToggle={toggleStage}
                counts={stageCounts}
              />
              <ChipGroup
                label="Type"
                options={typeOptions}
                selected={selectedTypes}
                onToggle={toggleType}
                counts={typeCounts}
              />
              <ChipGroup
                label="TLD"
                options={TLD_OPTIONS}
                selected={selectedTlds}
                onToggle={toggleTld}
                counts={tldCounts}
              />
            </div>

            <div className="flex items-center justify-between gap-3 border-t pt-3">
              <span className="text-xs text-muted-foreground">
                {isLoading
                  ? 'Loading deals...'
                  : `${deals.length.toLocaleString()} of ${allDeals.length.toLocaleString()} deals without an outcome`}
              </span>
              <Input
                type="search"
                placeholder="Search deals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 w-48 text-xs"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading
        ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" />
                  <span>Loading deals...</span>
                </div>
              </CardContent>
            </Card>
          )
        : (
            <AGDataGrid
              columnDefs={columns}
              rowData={deals}
              quickFilterText={searchQuery}
              domLayout="autoHeight"
              getRowId={(params) => params.data.id}
              overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No deals found</span>'
            />
          )}
    </Container>
  );
}
